import React, { useEffect, useState, useRef } from "react";
import PatientDetails from "./PatientDetails";
import { TabContext, TabPanel } from "@mui/lab";
import { Tabs, Tab, Grid2, IconButton, Box } from "@mui/material";
import PatientList from "./PatientList";
import CloseIcon from "@mui/icons-material/Close";
import NewPatient from "./NewPatient";
import EditPatient from "./EditPatient";
import PrescriptionReadOnlyView from "../Prescriptions/PrescriptionReadOnlyView";

function PatientHome() {
  const patientListRef = useRef();

  const openPrescriptionViewTab = (
    prescriptionId,
    prescriptionDisplayId,
    prescriptionType
  ) => {
    setTabs((prevTabs) => {
      const existingTab = tabs.find((tab) => tab.value === prescriptionId);
      if (existingTab) {
        setActiveTab(existingTab.value);
      } else {
        const newTab = {
          label: prescriptionDisplayId || "Prescription",
          value: prescriptionId,
          type: "ViewPrescription",
          prescriptionId: prescriptionId,
          prescriptionType: prescriptionType,
        };

        setActiveTab(prescriptionId);
        return [...prevTabs, newTab];
      }
      return prevTabs;
    });
  };

  // Define all functions before they are used
  const handleEditPatientTab = (patient) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find(
        (tab) => tab.value === `EditPatient-${patient.id}`
      );

      if (existingTab) {
        setActiveTab(existingTab.value);
        return prevTabs;
      }

      const newTab = {
        label: `Edit ${patient.firstName} ${patient.lastName}`,
        value: `EditPatient-${patient.id}`,
        type: "EditPatient",
        patient: patient,
        //content: <EditPatient patient={patient} onSave={handleSaveEdit} />,
      };

      setActiveTab(newTab.value);
      return [...prevTabs, newTab];
    });
  };

  const handleSaveEdit = (updatedPatient) => {
    // Close the edit tab
    const updatedTabs = tabs.filter(
      (tab) => !tab.value.startsWith("EditPatient")
    );
    setTabs(updatedTabs);

    // Switch back to the patient details tab
    const patientDetailsTab = tabs.find(
      (tab) => tab.value === `patient-${updatedPatient.id}`
    );
    if (patientDetailsTab) {
      setActiveTab(patientDetailsTab.value);
    } else {
      setActiveTab("Patients");
    }
  };

  const onNewPatientAdded = (tempNewPatientId) => {
    setTabs((prevTabs) =>
      prevTabs.filter((tab) => tab.value !== tempNewPatientId)
    );
    setActiveTab("Patients");
    patientListRef?.current?.refreshPatientList();
  };

  const handleViewDetails = (row) => {
    const existingTab = tabs.find((tab) => tab.value === row?.id);

    if (existingTab) {
      setActiveTab(existingTab.value);
      return;
    }

    const newTab = {
      label: `${row.firstName} ${row.lastName}`,
      value: row?.id,
      type: "PatientDetails",
      patientId: row?.id,
      //content: <PatientDetails patientId={row?.id} onOpenTab={handleEditPatientTab} />,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.value);
  };

  const handleCloseTab = (tabValue) => {
    if (tabValue === "Patients") return;
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tabValue));
    if (activeTab === tabValue) {
      setActiveTab("Patients");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNewPatientTab = () => {
    const newTabNumber = newPatientTabNumber + 1;
    setNewPatientTabNumber(newTabNumber);

    const newTab = {
      label: `New Patient (${newTabNumber})`,
      value: `NewPatient-${newTabNumber}`,
      type: "NewPatient",
      tempNewPatientId: `NewPatient-${newTabNumber}`,
      //content: <NewPatient tempNewPatientId={`NewPatient-${newTabNumber}`} onNewPatientAdded={onNewPatientAdded} />,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.value);
  };

  // State Initialization
  const [activeTab, setActiveTab] = useState(() => {
    const savedActiveTab = localStorage.getItem("activeTab");
    return savedActiveTab || "Patients"; // Default to "Patients" if no saved state
  });

  const [tabs, setTabs] = React.useState(() => {
    // Load tabs from local storage
    const savedTabs = localStorage.getItem("patients-tabs");
    return savedTabs
      ? JSON.parse(savedTabs)
      : [{ label: "Patients", value: "Patients" }];
  });

  const [newPatientTabNumber, setNewPatientTabNumber] = useState(0);

  // Effect Hooks
  useEffect(() => {
    localStorage.setItem("patients-tabs", JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  return (
    <Grid2 container>
      <TabContext value={activeTab}>
        <Grid2
          container
          alignItems="center"
          spacing={1}
          sx={{ borderBottom: "1px solid lightgray", width: "100%" }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Box>
                    {tab.label}
                    {tab.value !== "Patients" && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseTab(tab.value);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }
                value={tab.value}
              />
            ))}
          </Tabs>
        </Grid2>

        <Grid2 item size={12}>
          {tabs.map((tab) => (
            <TabPanel
              key={tab.value}
              value={tab.value}
              keepMounted={tab.value !== "Patients"}
              sx={{ padding: "0px", width: "100%" }}
            >
              {tab.value === "Patients" && (
                <PatientList
                  ref={patientListRef}
                  onViewDetails={handleViewDetails}
                  onEditPatient={handleEditPatientTab}
                  onNewPatient={handleNewPatientTab}
                />
              )}
              {tab.type === "PatientDetails" && (
                <PatientDetails
                  patientId={tab.patientId}
                  onOpenTab={handleEditPatientTab}
                  onViewDocument={openPrescriptionViewTab}
                />
              )}
              {tab.type === "ViewPrescription" && (
                <PrescriptionReadOnlyView
                  key={tab.prescriptionId}
                  prescriptionId={tab.prescriptionId}
                  prescriptionType={tab.prescriptionType}
                />
              )}
              {tab.type === "EditPatient" && (
                <EditPatient patient={tab.patient} onSave={handleSaveEdit} />
              )}
              {tab.type === "NewPatient" && (
                <NewPatient
                  tempNewPatientId={tab.tempNewPatientId}
                  onNewPatientAdded={onNewPatientAdded}
                />
              )}
              {tab.content && React.isValidElement(tab.content)
                ? tab.content
                : null}
            </TabPanel>
          ))}
        </Grid2>
      </TabContext>
    </Grid2>
  );
}

export default PatientHome;
